import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, tap } from 'rxjs';
import { baseUrl } from '../constants';

interface AuthResponse {
  token?: string;
  email?: string;
  role?: string;
  permissions?: string[];
  message?: string;
  exp?: number;
  userId?: string;
  fullName?: string;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private baseUrl = `${baseUrl}Auth`;
  private currentUser: AuthResponse | null = null;

  constructor(private http: HttpClient) {
    this.restoreUser(); // ✅ Rehydrate on app start
  }

  /** 🔹 LOGIN */
  login(credentials: { email: string; password: string }): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.baseUrl}/login`, credentials).pipe(
      tap(user => {
        if (user?.token) {
          console.log('%c[AuthService] ✅ Login successful, saving user...', 'color: green');
          
          this.saveUser(user);
        } else {
          console.warn('%c[AuthService] ⚠️ No token received on login response', 'color: orange');
        }
      })
    );
  }

  /** 🔹 SAVE USER TO LOCALSTORAGE */
  saveUser(user: AuthResponse): void {
    this.currentUser = user;
    localStorage.setItem('user', JSON.stringify(user));
    if (user.token) {
      localStorage.setItem('token', user.token);
    }
  }

  /** 🔹 RESTORE USER (on refresh) */
  private restoreUser(): void {
    const stored = localStorage.getItem('user');
    if (stored) {
      try {
        this.currentUser = JSON.parse(stored);
      } catch (err) {
        console.error('[AuthService] ❌ Failed to parse stored user', err);
        this.currentUser = null;
      }
    }
  }

  /** 🔹 LOGOUT */
  logout(): void {
    this.currentUser = null;
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    console.log('%c[AuthService] 👋 Logged out, user cleared.', 'color: gray');
  }

  /** 🔹 GET CURRENT USER */
  getCurrentUser(): AuthResponse | null {
    if (this.currentUser) return this.currentUser;

    const stored = localStorage.getItem('user');
    if (stored) {
      try {
        this.currentUser = JSON.parse(stored);
        return this.currentUser;
      } catch {
        console.error('[AuthService] ❌ Corrupted user data in localStorage');
        return null;
      }
    }
    return null;
  }

  /** 🔹 GET TOKEN */
  getToken(): string | null {
    const token = localStorage.getItem('token');
    if (token) return token;

    const user = this.getCurrentUser();
    return user?.token || null;
  }

  /** 🔹 DECODE JWT TO GET ROLE & PERMISSIONS */
  getUser() {
    const token = this.getToken();
    if (!token) {
      console.warn('[AuthService] ⚠️ No token found when calling getUser()');
      return null;
    }

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));

   
      // --- Consolidate all permissions and roles into a single set ---
      const claims = new Set<string>();
      // Helper function to add single or array of strings to the set
      const processClaim = (claimValue: any) => {
        if (Array.isArray(claimValue)) {
          claimValue.forEach(v => typeof v === 'string' && claims.add(v.toLowerCase()));
        } else if (typeof claimValue === 'string') {
          claims.add(claimValue.toLowerCase());
        }
      };

      // Process all known properties for roles and permissions from the JWT payload
      processClaim(payload.permissions || payload.Permissions);
      processClaim(payload.role || payload.Role);
      processClaim(payload.roles || payload.Roles);
      processClaim(payload['http://schemas.microsoft.com/ws/2008/06/identity/claims/role']);

      // --- Determine the primary role ---
      // This is for logic like isAdmin(). We prioritize 'admin' if it exists in any claim.
      const primaryRole = claims.has('admin') ? 'admin' : (payload.role || payload.Role || 'user');


      return {
        userId: payload.userId || payload.sub || '',
        email: payload.email || payload.sub || '',
        fullName: payload.fullName || payload.name || payload.unique_name || '',
        role: primaryRole.toLowerCase(),
        permissions: Array.from(claims), // Return all collected claims as permissions
        exp: payload.exp
      };
    } catch (err) {
      console.error('[AuthService] ❌ Failed to decode JWT', err);
      return null;
    }
  }

  /** 🔹 CHECK AUTHENTICATION (with expiry validation) */
  isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) {
      console.warn('[AuthService] ⚠️ No token found (unauthenticated)');
      return false;
    }

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const now = Math.floor(Date.now() / 1000);

      if (payload.exp && payload.exp < now) {
        console.warn('[AuthService] ⚠️ Token expired, logging out');
        this.logout();
        return false;
      }

      return true;
    } catch (err) {
      console.error('[AuthService] ❌ Invalid token format', err);
      this.logout();
      return false;
    }
  }

  /** 🔹 ROLE CHECK */
  isAdmin(): boolean {
    const user = this.getUser();
    return user?.role === 'admin';
  }

  /** 🔹 GET PERMISSIONS */
  getPermissions(): string[] {
    const user = this.getUser();
    return user?.permissions || [];
  }

  /** 🔹 PERMISSION CHECK (Admin override) */
  hasPermission(permission: string): boolean {
    const user = this.getUser();
    if (!user) {
      console.warn('[AuthService] ⚠️ hasPermission called without user');
      return false;
    }

    if (this.isAdmin()) return true;

    const perms = this.getPermissions();
    return perms.includes(permission.toLowerCase());
  }

  hasAnyPermission(permissions: string[]): boolean {
    if (this.isAdmin()) return true;
    const claims = new Set(this.getPermissions().map((value) => value.toLowerCase().trim()));
    const role = this.getRole()?.toLowerCase().trim();
    if (role) claims.add(role);
    return permissions.some((permission) => claims.has(permission.toLowerCase().trim()));
  }

  getPreferredLandingRoute(): string {
    if (this.hasAnyPermission(['dashboard'])) return '/dashboard';

    const moduleRoutes: Array<{ claims: string[]; route: string }> = [
      { claims: ['project.view', 'project.manage', 'project.task.manage', 'project.onboarding.manage', 'project.client.onboarding', 'client'], route: '/projects/overview' },
      { claims: ['staff.view', 'staff.manage', 'staff.request.view', 'staff.request.approve'], route: '/staff/overview' },
      { claims: ['staff.message.view', 'staff.message.send'], route: '/communication/mails' },
      { claims: ['manage_logistics', 'manage_delivery', 'manage_riders', 'manage_vehicles', 'manage_maintenance', 'manage_activities', 'dispatcher', 'rider'], route: '/logistics/overview' },
      { claims: ['manage_supply', 'supply.products.manage', 'supply.dispatch.manage', 'supply.history.view', 'supply.analytics.view'], route: '/supply/overview' },
      { claims: ['view_reports'], route: '/reports' },
      { claims: ['manage_users'], route: '/users' },
      { claims: ['manage_roles'], route: '/roles' }
    ];

    const matched = moduleRoutes.find((entry) => this.hasAnyPermission(entry.claims));
    return matched?.route || '/unauthorized';
  }

  /** 🔹 AUTO RELOGIN SUPPORT */
  refreshUserFromStorage(): void {
    const stored = localStorage.getItem('user');
    if (stored) {
      try {
        this.currentUser = JSON.parse(stored);
        console.log('%c[AuthService] ♻️ User restored from storage.', 'color: teal');
      } catch {
        this.currentUser = null;
      }
    }
  }
  getRole(): string | null {
    const user = this.getUser();
    return user?.role || null;
  }
}
